import React, { useState, useRef, useEffect } from "react";
import { Drawer, Card, Typography, Tag, Alert, Input, message, Tooltip, Image } from "antd";
import {
    KeyOutlined, SafetyOutlined, IdcardOutlined, BarcodeOutlined,
    CheckCircleFilled, PictureOutlined, WarningOutlined,
} from "@ant-design/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose, faUpload, faPlus } from "@fortawesome/free-solid-svg-icons";
import { useMutation, useQuery } from "@apollo/client";
import { reportKeyDamagedMutation } from "../../util/mutation/keyRegister.mutation";
import { genericFileUploadSignatureMutation } from "@src/util/mutation/genericFileUpload.mutation";
import axios from "axios";
import dayjs from "dayjs";
import { getKeyByIdQuery, getKeyUnitStatusesQuery } from "../../util/query/keyRegister.query";

const { Text, Title } = Typography;
const { TextArea } = Input;

const KEY_TYPE_CONFIG = {
    metal:      { label: "Metal",       icon: <KeyOutlined />,      color: "#8c8c8c" },
    fob:        { label: "Fob",         icon: <SafetyOutlined />,   color: "#1677ff" },
    card:       { label: "Card",        icon: <IdcardOutlined />,   color: "#722ed1" },
    accessCode: { label: "Access Code", icon: <BarcodeOutlined />,  color: "#13c2c2" },
};

const MAX_IMAGES = 5;
const MAX_FILE_SIZE_MB = 5;

// Inline validation text — shown on the form itself instead of a toast.
const FieldError = ({ children }) => !children ? null : (
    <div style={{ color: "#ff4d4f", fontSize: 12, marginTop: 6 }}>{children}</div>
);

// ─────────────────────────────────────────────────────────────────────────────
export const ReportKeyDamagedModal = ({ visible, close, keyId, onComplete }) => {

    const { data, loading } = useQuery(getKeyByIdQuery, {
        variables: { keyId },
        skip: !keyId || !visible,
        fetchPolicy: "network-only",
    });
    const keyData = data?.getKeyById?.response;

    const { data: unitStatusData, loading: unitStatusLoading } = useQuery(getKeyUnitStatusesQuery, {
        variables: { keyId },
        skip: !keyId || !visible,
        fetchPolicy: "network-only",
    });
    const unitStatuses = unitStatusData?.getKeyUnitStatuses?.response || [];

    const [currentStep, setCurrentStep] = useState(0);
    const [target, setTarget]           = useState(null); // { keyType, scope: "single"|"all", unitIndex }
    const [reason, setReason]           = useState("");
    const [images, setImages]           = useState([]); // [{ uid, file, name, preview }]
    const [submitted, setSubmitted]     = useState(false);
    const [stepError, setStepError]     = useState("");
    // submitLoading/uploadSigLoading only cover their own GraphQL calls, not the
    // axios.put S3 uploads in between — track the whole submit explicitly.
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef(null);

    const [reportKeyDamaged, { loading: submitLoading }]              = useMutation(reportKeyDamagedMutation);
    const [genericFileUploadSignature, { loading: uploadSigLoading }] = useMutation(genericFileUploadSignatureMutation);

    const keyTypes  = keyData?.keyTypes || [];
    const siteName  = keyData?.site?.sitename        || "—";
    const keySerial = keyData?.keySerial             || "—";
    const custodian = keyData?.custodianDetail?.name || "—";
    const siteId    = keyData?.siteId                || "";

    const beforeUnitStatus = target
        ? unitStatuses.find(u => u.keyType === target.keyType && (target.scope === "all" || u.index === target.unitIndex))
        : null;
    const beforeImages = beforeUnitStatus?.lastImages || [];

    const steps = [{ title: "Select Key" }, { title: "Circumstances" }, { title: "Photo Evidence" }];
    const isLastStep = currentStep === steps.length - 1;

    useEffect(() => {
        if (visible) {
            setCurrentStep(0);
            setTarget(null);
            setReason("");
            setImages([]);
            setSubmitted(false);
            setStepError("");
        }
    }, [visible]);

    // Clear the inline error whenever the step changes, and as soon as the
    // thing being complained about is fixed.
    useEffect(() => { setStepError(""); }, [currentStep]);
    useEffect(() => { if (target) setStepError(""); }, [target]);
    useEffect(() => { if (reason.trim()) setStepError(""); }, [reason]);
    useEffect(() => { if (images.length) setStepError(""); }, [images.length]);

    useEffect(() => {
        return () => { images.forEach(img => img.preview && URL.revokeObjectURL(img.preview)); };
    }, []);

    // ── Image helpers (same shape as newKey.modal.jsx / keyAuditConduct.modal.jsx) ──
    const validateFile = (file) => {
        if (!file.type.startsWith("image/")) { message.error(`"${file.name}" is not an image file`); return false; }
        if (file.size / 1024 / 1024 >= MAX_FILE_SIZE_MB) { message.error(`"${file.name}" exceeds ${MAX_FILE_SIZE_MB} MB limit`); return false; }
        return true;
    };

    const addImageFiles = (files) => {
        const remaining = MAX_IMAGES - images.length;
        if (remaining <= 0) { message.warning(`Maximum ${MAX_IMAGES} images allowed`); return; }
        const valid = Array.from(files).slice(0, remaining).filter(validateFile);
        setImages(prev => [
            ...prev,
            ...valid.map(file => ({
                uid: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                file,
                name: file.name,
                preview: URL.createObjectURL(file),
            })),
        ]);
    };

    const removeImage = (uid) => {
        setImages(prev => {
            const img = prev.find(i => i.uid === uid);
            if (img?.preview) URL.revokeObjectURL(img.preview);
            return prev.filter(i => i.uid !== uid);
        });
    };

    const handleNext = () => {
        const label = steps[currentStep]?.title;
        if (label === "Select Key" && !target) {
            setStepError("Please select which key is damaged");
            return;
        }
        if (label === "Circumstances" && !reason.trim()) {
            setStepError("Please describe how the key was damaged");
            return;
        }
        setCurrentStep(s => s + 1);
    };

    const handleSubmit = async () => {
        if (!images.length) { setStepError("Please capture at least one photo of the damage"); return; }
        setIsUploading(true);
        try {
            const afterImages = [];
            await Promise.all(images.map(async (img) => {
                const imgName = `keys/${siteId}/${keySerial}-${dayjs().valueOf()}-${img.name.replaceAll(" ", "-")}`.toLowerCase();
                const urlRes = await genericFileUploadSignature({ variables: { input: { fileName: imgName, objectType: "keys" } } });
                const uploadResponse = urlRes?.data?.genericFileUploadSignatureUrl?.response;
                await axios.put(uploadResponse?.url, img.file, { headers: { "Content-Type": img.file.type } });
                // The server strips whitespace from the filename before uploading —
                // store the name it actually used, not our local guess.
                afterImages.push(uploadResponse?.originalFileName || imgName);
            }));

            await reportKeyDamaged({
                variables: {
                    input: {
                        keyId,
                        keyType: target.keyType,
                        scope: target.scope,
                        unitIndex: target.scope === "single" ? target.unitIndex : null,
                        reason,
                        afterImages,
                    },
                },
            });

            if (onComplete) onComplete();
            setSubmitted(true);
        } catch (err) {
            message.error(err?.message || "Failed to report key as damaged");
        } finally {
            setIsUploading(false);
        }
    };

    // ── Step 0 — Select Key ─────────────────────────────────────────────────
    const renderStep0 = () => (
        <div data-tour="reportDamagedSelectStep" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <Card style={{ borderRadius: 10, background: "#fff2f0", border: "1px solid #ffccc7" }} bodyStyle={{ padding: "16px 20px" }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {[
                        { label: "Site",       value: siteName },
                        { label: "Key Serial", value: <Text code style={{ fontSize: 13 }}>{keySerial}</Text> },
                        { label: "Custodian",  value: custodian },
                    ].map(row => (
                        <div key={row.label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>{row.label}</Text>
                            <Text strong style={{ fontSize: 13 }}>{row.value}</Text>
                        </div>
                    ))}
                </div>
            </Card>

            <Text type="secondary" style={{ fontSize: 13 }}>
                Which key is damaged? Pick a single key, or the whole set of a type if all of them are affected.
            </Text>

            {keyTypes.map(kt => {
                const cfg   = KEY_TYPE_CONFIG[kt.type] || {};
                const count = kt.count || 1;
                const units = unitStatuses.filter(u => u.keyType === kt.type);

                return (
                    <Card key={kt.type} style={{ borderRadius: 10, border: "1px solid #f0f0f0" }} bodyStyle={{ padding: "14px 18px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                            <div style={{
                                width: 36, height: 36, borderRadius: 8, background: `${cfg.color}18`,
                                display: "flex", alignItems: "center", justifyContent: "center", color: cfg.color, fontSize: 15,
                            }}>
                                {cfg.icon}
                            </div>
                            <Text strong style={{ fontSize: 14 }}>{cfg.label} Keys <Text type="secondary" style={{ fontWeight: 400 }}>× {count}</Text></Text>
                        </div>

                        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                            {Array.from({ length: count }).map((_, index) => {
                                const isActive = target?.keyType === kt.type && target?.scope === "single" && target?.unitIndex === index;
                                const unit = units.find(u => u.index === index);
                                const label = `${cfg.label} #${index + 1}`;
                                return (
                                    <Tooltip key={index} title={unit?.status === "live" || !unit ? "Currently live" : `Last event: ${unit.status}`}>
                                        <button
                                            type="button"
                                            onClick={() => setTarget({ keyType: kt.type, scope: "single", unitIndex: index })}
                                            style={{
                                                padding: "6px 14px", borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: 500,
                                                border: isActive ? "1.5px solid #ff4d4f" : "1px solid #d9d9d9",
                                                background: isActive ? "#ff4d4f" : "#fff",
                                                color: isActive ? "#fff" : "#595959",
                                            }}
                                        >
                                            {label}
                                            {unit && unit.status !== "live" && (
                                                <Tag color={unit.status === "replaced" ? "warning" : "error"} style={{ marginLeft: 6, marginRight: 0, borderRadius: 3, fontSize: 10, lineHeight: "16px" }}>
                                                    {unit.status}
                                                </Tag>
                                            )}
                                        </button>
                                    </Tooltip>
                                );
                            })}

                            {count > 1 && (
                                <button
                                    type="button"
                                    onClick={() => setTarget({ keyType: kt.type, scope: "all", unitIndex: null })}
                                    style={{
                                        padding: "6px 14px", borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: 500,
                                        border: target?.keyType === kt.type && target?.scope === "all" ? "1.5px solid #ff4d4f" : "1px dashed #d9d9d9",
                                        background: target?.keyType === kt.type && target?.scope === "all" ? "#ff4d4f" : "#fff",
                                        color: target?.keyType === kt.type && target?.scope === "all" ? "#fff" : "#595959",
                                    }}
                                >
                                    All {cfg.label} Keys
                                </button>
                            )}
                        </div>
                    </Card>
                );
            })}

            <FieldError>{stepError}</FieldError>
        </div>
    );

    // ── Step 1 — Circumstances ───────────────────────────────────────────────
    const renderStep1 = () => (
        <div data-tour="reportDamagedReasonStep" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <Alert
                type="warning"
                showIcon
                message={target?.scope === "all"
                    ? `All ${KEY_TYPE_CONFIG[target.keyType]?.label} Keys`
                    : `${KEY_TYPE_CONFIG[target?.keyType]?.label} Key #${(target?.unitIndex ?? 0) + 1}`}
                description="Describe how the key was damaged."
                style={{ borderRadius: 8 }}
            />
            <TextArea
                rows={4}
                placeholder="e.g. Bent while forcing a stuck lock"
                className="generic-input-control"
                value={reason}
                onChange={e => setReason(e.target.value)}
            />
            <FieldError>{stepError}</FieldError>
        </div>
    );

    // ── Step 2 — Photo Evidence ──────────────────────────────────────────────
    const renderStep2 = () => (
        <div data-tour="reportDamagedPhotoStep" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {beforeImages.length > 0 && (
                <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                        <Text strong style={{ fontSize: 13 }}>Before</Text>
                        <Tag style={{ borderRadius: 4, fontSize: 11 }}>
                            {beforeUnitStatus?.imageSource === "registration" ? "Registration photo" : "Last recorded"}
                        </Tag>
                    </div>
                    <Image.PreviewGroup>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                            {beforeImages.map((src, i) => (
                                <Image key={i} src={src} width={88} height={88} style={{ objectFit: "cover", borderRadius: 8, border: "1px solid #e8e8e8" }} />
                            ))}
                        </div>
                    </Image.PreviewGroup>
                </div>
            )}

            <div>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                    <PictureOutlined style={{ color: "#ff4d4f" }} />
                    <Text strong style={{ fontSize: 13 }}>
                        Damage Photo{images.length !== 1 ? "s" : ""}{" "}
                        <span style={{ color: "#999", fontWeight: 400 }}>({images.length}/{MAX_IMAGES})</span>
                    </Text>
                    <Text type="danger" style={{ fontSize: 12 }}>*</Text>
                </div>

                <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                    {images.map(img => (
                        <div key={img.uid} style={{ position: "relative", width: 88, height: 88, borderRadius: 8, overflow: "hidden", border: "1px solid #e8e8e8" }}>
                            <img src={img.preview} alt={img.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                            <button
                                type="button"
                                onClick={() => removeImage(img.uid)}
                                style={{
                                    position: "absolute", top: 4, right: 4, background: "rgba(0,0,0,0.55)", border: "none",
                                    borderRadius: "50%", width: 20, height: 20, display: "flex", alignItems: "center",
                                    justifyContent: "center", cursor: "pointer", padding: 0,
                                }}
                            >
                                <FontAwesomeIcon icon={faClose} style={{ color: "#fff", fontSize: 10 }} />
                            </button>
                        </div>
                    ))}

                    {images.length < MAX_IMAGES && (
                        <div
                            onClick={() => fileInputRef.current?.click()}
                            style={{
                                width: 88, height: 88, borderRadius: 8, border: "2px dashed #d9d9d9",
                                display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                                cursor: "pointer", background: "#fafafa", gap: 4,
                            }}
                        >
                            <FontAwesomeIcon icon={images.length ? faPlus : faUpload} style={{ fontSize: 16, color: "#8c8c8c" }} />
                            <span style={{ fontSize: 11, color: "#8c8c8c" }}>{images.length ? "Add More" : "Upload"}</span>
                        </div>
                    )}
                </div>

                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    style={{ display: "none" }}
                    onChange={e => { if (e.target.files?.length) addImageFiles(e.target.files); e.target.value = ""; }}
                />
                <FieldError>{stepError}</FieldError>
            </div>
        </div>
    );

    const renderSuccess = () => (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 20, textAlign: "center", height: "60vh" }}>
            <CheckCircleFilled style={{ fontSize: 72, color: "#52c41a" }} />
            <div>
                <Title level={3} style={{ margin: 0, color: "#262626" }}>Damage Reported!</Title>
                <Text type="secondary" style={{ fontSize: 14 }}>This has been added to the key's history.</Text>
            </div>
        </div>
    );

    const stepContents = [renderStep0(), renderStep1(), renderStep2()];
    const isSubmitting = submitLoading || uploadSigLoading || isUploading;

    return (
        <Drawer
            title={<>Report Key Damaged</>}
            placement="right"
            closable={true}
            onClose={close}
            closeIcon={<FontAwesomeIcon icon={faClose} className="close-icon" />}
            visible={visible}
            width={560}
            maskClosable={false}
            mask={true}
            loading={loading || unitStatusLoading}
            footer={
                !submitted && (
                    <div className="drawer-footer" style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
                        {currentStep > 0 && (
                            <button className="drawer-outlined-btn" onClick={() => setCurrentStep(s => s - 1)} disabled={isSubmitting}>
                                Previous
                            </button>
                        )}
                        {!isLastStep ? (
                            <button data-tour="reportDamagedNextBtn" className="drawer-filled-btn" onClick={handleNext}>Next</button>
                        ) : (
                            <button data-tour="reportDamagedSubmitBtn" className="drawer-filled-btn" onClick={handleSubmit} disabled={isSubmitting}>
                                {isSubmitting ? "Submitting…" : "Submit"}
                            </button>
                        )}
                    </div>
                )
            }
        >
            {submitted ? (
                <div data-tour="reportDamagedSubmittedStep">{renderSuccess()}</div>
            ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                    <Text strong style={{ fontSize: 16 }}>{steps[currentStep]?.title}</Text>
                    <div>{stepContents[currentStep]}</div>
                </div>
            )}
        </Drawer>
    );
};
